import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../lib/cn";
import { LABEL_SKIN } from "./FieldLabel";
import { useFormSkin } from "./FormSkinContext";
import { useRequiredIndicator } from "./RequiredIndicatorContext";

/** Radio classes per form skin (see FormSkinContext). `legend` adds to the LABEL_SKIN label classes. */
const RADIO_SKIN = {
  styleguide: {
    row: "flex flex-wrap gap-4",
    option: "text-foreground flex cursor-pointer items-center gap-2 text-sm",
    radio: "size-5 checked:border-[6px]",
    legend: "",
  },
  enroll: {
    row: "flex flex-wrap gap-x-[26px] gap-y-3",
    option: "text-midnight flex cursor-pointer items-center gap-2.5 text-[15px] font-medium",
    radio: "size-[26px] checked:border-[8px]",
    // cn() applies these after LABEL_SKIN.enroll and the base `mb-1.5`, so
    // twMerge lets them win.
    legend: "mb-4 text-[14px]",
  },
} as const;

interface RadioOption {
  value: string;
  label: string;
}

interface RadioGroupProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Group caption rendered as the fieldset's <legend>. */
  legend: string;
  options: RadioOption[];
  error?: string | undefined;
  /** Render an asterisk after the legend text, independent of the native `required` attribute. */
  required?: boolean | undefined;
  /** Content right after the legend text (e.g. a Badge), outside its label type styles. */
  legendAddon?: ReactNode | undefined;
  /**
   * react-hook-form's `register()` returns a single callback `ref`. The
   * registered props (`name`, `onChange`, `onBlur`, `ref`) are spread onto every
   * radio so they share one group name. React 19 accepts `ref` as a plain prop.
   */
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * Radio group in a fieldset, usable controlled or with react-hook-form.
 *
 *   - Controlled: `<RadioGroup value={state} onChange={…} />`. Each radio
 *     keeps its own `value={opt.value}`, and `checked` is derived as
 *     `value === opt.value`, so the selected option reflects React state.
 *   - Uncontrolled or react-hook-form: `<RadioGroup {...register("foo")} />`.
 *     `register()` returns `{name, onChange, onBlur, ref}` and passes no
 *     `value`/`checked`. The shared `name` and the forwarded `ref` drive
 *     selection natively, and `checked` stays undefined.
 */
export function RadioGroup({
  ref,
  legend,
  options,
  error,
  required,
  legendAddon,
  className,
  id,
  // Destructured out of `...rest` so the spread can't clobber the per-option
  // `value`. `value` is the controlled group selection (a string when present);
  // `checked`/`defaultChecked` would be meaningless at the group level.
  value: controlledValue,
  checked: _checked,
  defaultChecked: _defaultChecked,
  ...rest
}: RadioGroupProps): ReactNode {
  const generatedId = useId();
  const groupId = id ?? generatedId;
  const errorId = error ? `${groupId}-error` : undefined;
  const isControlled = controlledValue !== undefined;
  // Opt-in "(required)" wording (see RequiredIndicatorContext); null = asterisk only.
  const requiredWord = useRequiredIndicator();
  const formSkin = useFormSkin();
  const labelSkin = LABEL_SKIN[formSkin];
  const skin = RADIO_SKIN[formSkin];
  const caption = (
    <>
      {legend}
      {required && (
        <span aria-hidden="true" className={labelSkin.asterisk}>
          *
        </span>
      )}
      {required && requiredWord && (
        <span className="text-slate-teal ml-1 font-normal">({requiredWord})</span>
      )}
    </>
  );
  return (
    <fieldset aria-invalid={error ? true : undefined} aria-describedby={errorId}>
      {legendAddon === undefined ? (
        <legend className={cn("mb-1.5", labelSkin.label, skin.legend)}>{caption}</legend>
      ) : (
        // The caption carries the label type, so the addon doesn't inherit its
        // uppercase and tracking, as beside a FieldLabel.
        <legend className="mb-1.5 flex items-center gap-1.5">
          <span className={cn(labelSkin.label, skin.legend)}>{caption}</span>
          {legendAddon}
        </legend>
      )}
      <div className={skin.row}>
        {options.map((opt) => (
          <label key={opt.value} className={skin.option}>
            <input
              ref={ref}
              type="radio"
              value={opt.value}
              // Controlled: derive per-option checked. Uncontrolled/RHF: leave
              // `checked` absent (only spread it when we have a real boolean, to
              // satisfy `exactOptionalPropertyTypes`).
              {...(isControlled ? { checked: controlledValue === opt.value } : {})}
              {...rest}
              className={cn(
                "border-cloud-blue shrink-0 cursor-pointer appearance-none rounded-full border-2 bg-white transition-colors",
                "checked:border-ember",
                skin.radio,
                error && "border-danger",
                className,
              )}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-danger mt-1 text-[13px] leading-[1.5]">
          {error}
        </p>
      )}
    </fieldset>
  );
}
